import { HttpClient, HttpHeaders, HttpResponse, HttpStatusCode } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { catchError, EMPTY, filter, map, Observable, take, tap } from "rxjs";
import { ConfirmationDialogComponent } from "../components/dialogs/confirmation-dialog/confirmation-dialog.component";
import { RequestHeader } from "../enums";
import { ConfirmationDialogData } from "../models/confirmation-dialog-data";
import { ETagStorageService } from "./etag-storage.service";

@Injectable({ providedIn: 'root' })
export class HttpConcurrencyClient {
  private readonly eTagValidationFailedConfirmDialog: ConfirmationDialogData = {
    title: 'Refresh Required',
    content: 'The information on this page is no longer up to date. To continue, please refresh the page and try again.',
    confirmBtnName: 'Refresh',
    cancelBtnName: 'Cancel',
    hideCancelBtn: false
  };

  public readonly http = inject(HttpClient);
  private readonly matDialog = inject(MatDialog);
  private readonly eTagStore = inject(ETagStorageService);

  /**
   * Performs a GET request and stores the ETag from the response headers.
   */
  getWithETag<T>(
    url: string,
    resourceETagKey: string,
    headers: { [header: string]: string | string[]; } = {}): Observable<T> {
    return this.http.get<T>(url, { observe: 'response', headers }).pipe(
      tap((res: HttpResponse<T>) => {
        const eTag = res.headers.get(RequestHeader.ETag);

        if (eTag) {
          this.eTagStore.set(resourceETagKey, eTag);
        } else {
          throw new Error("Returned ETag is undefined.");
        }
      }),
      map(res => res.body as T)
    );
  }

  /**
   * Performs a POST request and stores the ETag using the returned object's ID.
   */
  postWithETag<T>(
    url: string,
    body: any): Observable<T> {
    return this.http.post<T>(url, body, { observe: 'response' }).pipe(
      tap((res: HttpResponse<T>) => {
        const id = (res.body as any).id;
        if (!id) {
          throw new Error("Return value does not have id value.");
        }

        const eTag = res.headers.get(RequestHeader.ETag);
        if (eTag) {
          this.eTagStore.set(id, eTag);
        } else {
          throw new Error("Returned ETag is undefined.");
        }
      }),
      map(res => res.body as T)
    );
  }

  /**
   * Performs a PUT request with If-Match header for concurrency control.
   */
  putIfMatch<T>(url: string, body: any, resourceETagKey: string): Observable<T> {
    const headers = this.getIfMatchHeaders(resourceETagKey);

    return this.http.put<T>(url, body, { headers, observe: 'response' }).pipe(
      tap((res: HttpResponse<T>) => {
        const eTag = res.headers.get(RequestHeader.ETag);

        if (eTag) {
          this.eTagStore.set(resourceETagKey, eTag);
        } else {
          throw new Error("Returned ETag is undefined.");
        }
      }),
      catchError(err => {
        if (err?.status === HttpStatusCode.PreconditionFailed) {
          this.handlePreconditionFailed();

          return EMPTY;
        } else {
          throw err;
        }
      }),
      map(res => res.body as T)
    );
  }

  /**
   * Performs a POST request with If-Match header for concurrency control.
   */
  postIfMatch<T>(url: string, body: any, resourceETagKey: string): Observable<T> {
    const headers = this.getIfMatchHeaders(resourceETagKey);

    return this.http.post<T>(url, body, { headers, observe: 'response' }).pipe(
      tap((res: HttpResponse<T>) => {
        const eTag = res.headers.get(RequestHeader.ETag);

        if (eTag) {
          this.eTagStore.set(resourceETagKey, eTag);
        } else {
          throw new Error("Returned ETag is undefined.");
        }
      }),
      catchError(err => {
        if (err?.status === HttpStatusCode.PreconditionFailed) {
          this.handlePreconditionFailed();

          return EMPTY;
        } else {
          throw err;
        }
      }),
      map(res => res.body as T)
    );
  }

  /**
   * Builds headers with the stored ETag for If-Match validation.
   */
  private getIfMatchHeaders(resourceETagKey: string): HttpHeaders {
    const eTag = this.eTagStore.get(resourceETagKey);

    return new HttpHeaders({ [RequestHeader.IfMatch]: eTag });
  }

  /**
   * Handles 412 Precondition Failed by showing a confirmation dialog and reloading the page if user confirms.
   */
  private handlePreconditionFailed() {
    const dialogRef = this.matDialog.open(ConfirmationDialogComponent, {
      disableClose: true,
      data: this.eTagValidationFailedConfirmDialog
    });

    dialogRef.afterClosed().pipe(take(1), filter(result => !!result)).subscribe(
      () => location.reload()
    );
  }
}
